/**
 * «Лабораторія»: interactive simulations of PhET (University of Colorado Boulder, CC BY 4.0 —
 * free to use with the authors named) in Ukrainian, and our own 3D models. The catalog is part of
 * the app: what fits which subject and class, what to try. A simulation added to the materials of
 * a course is a plain link to it, so every app recognises it by the address.
 */

export const PHET_BASE = 'https://phet.colorado.edu/sims/html';
export const PHET_SITE = 'https://phet.colorado.edu';
/** CC BY 4.0: shown next to every simulation */
export const PHET_CREDIT =
  'PhET Interactive Simulations, University of Colorado Boulder · CC BY 4.0';

/** a subject as the school names it, and the classes the item fits */
export type LabFit = readonly [subject: string, from: number, to: number];

export interface LabSim {
  slug: string;
  title: string;
  /** what it shows, one line */
  about: string;
  fits: readonly LabFit[];
  /** stems of lesson topics it helps with, lower case */
  topics: readonly string[];
  /** «Спробуй»: small experiments to start with */
  tries: readonly string[];
}

const PHYSICS = 'Фізика';
const CHEMISTRY = 'Хімія';
const MATH = 'Математика';
const ALGEBRA = 'Алгебра';
const GEOMETRY = 'Геометрія';
const CALCULUS = 'Алгебра і початки аналізу';
const NATURE = 'Пізнаємо природу';
const BIOLOGY = 'Біологія';
const BIOLOGY_ECO = 'Біологія і екологія';
const GEOGRAPHY = 'Географія';

export const LAB_SIMS: readonly LabSim[] = [
  // --- Фізика -----------------------------------------------------------------------------------
  {
    slug: 'forces-and-motion-basics',
    title: 'Сили і рух: основи',
    about: 'Перетягування каната, тертя і прискорення: як сили змінюють рух.',
    fits: [
      [PHYSICS, 7, 7],
      [NATURE, 5, 6],
    ],
    topics: ['сил', 'рух', 'взаємоді'],
    tries: [
      'Розстав команди на канаті так, щоб візок не рушив з місця.',
      'Штовхай шафу з тертям і без нього. Коли вона рушає?',
    ],
  },
  {
    slug: 'friction',
    title: 'Тертя',
    about: 'Що відбувається з атомами, коли одна поверхня треться об іншу.',
    fits: [[PHYSICS, 7, 7]],
    topics: ['сил', 'терт', 'тепл'],
    tries: [
      'Потри книжки одна об одну. Що стається з температурою?',
      'Потри дуже довго — чому атоми починають відриватися?',
    ],
  },
  {
    slug: 'density',
    title: 'Густина',
    about: 'Чому одні тіла тонуть, а інші плавають: маса, об’єм і густина.',
    fits: [
      [PHYSICS, 7, 7],
      [NATURE, 5, 6],
    ],
    topics: ['густин', 'речовин', 'тіла', 'практикум'],
    tries: [
      'Знайди, з якого матеріалу кожен таємничий брусок.',
      'Чому брусок тієї ж маси може і тонути, і плавати?',
    ],
  },
  {
    slug: 'buoyancy',
    title: 'Виштовхувальна сила',
    about: 'Сила Архімеда: від чого залежить, чи спливе тіло.',
    fits: [[PHYSICS, 7, 7]],
    topics: ['тиск', 'архімед', 'плаван', 'паскал'],
    tries: [
      'Порівняй, з якою силою вода виштовхує деревину і цеглу однакового об’єму.',
      'Заміни воду на мед. Що змінилося?',
    ],
  },
  {
    slug: 'under-pressure',
    title: 'Під тиском',
    about: 'Тиск рідини на різній глибині і в посудинах різної форми.',
    fits: [[PHYSICS, 7, 7]],
    topics: ['тиск', 'паскал'],
    tries: [
      'Опусти манометр глибше. Як змінюється тиск?',
      'Чи залежить тиск на дні від форми посудини?',
    ],
  },
  {
    slug: 'balancing-act',
    title: 'Балансування',
    about: 'Важіль і рівновага: маса, відстань і момент сили.',
    fits: [
      [PHYSICS, 7, 7],
      [MATH, 5, 6],
    ],
    topics: ['важіл', 'рівноваг', 'момент', 'практикум'],
    tries: [
      'Зрівноваж цеглину 10 кг цеглиною 5 кг.',
      'Знайди масу таємничого предмета за допомогою важеля.',
    ],
  },
  {
    slug: 'hookes-law',
    title: 'Закон Гука',
    about: 'Як пружина розтягується під дією сили.',
    fits: [[PHYSICS, 7, 7]],
    topics: ['пружн', 'сил', 'гук'],
    tries: [
      'Потягни пружину вдвічі сильніше. На скільки вона подовшала?',
      'З’єднай дві пружини послідовно і паралельно — яка система жорсткіша?',
    ],
  },
  {
    slug: 'energy-skate-park-basics',
    title: 'Парк для скейтів: основи',
    about: 'Кінетична і потенціальна енергія на рампі.',
    fits: [[PHYSICS, 7, 7]],
    topics: ['енергі', 'робот'],
    tries: [
      'Запусти скейтера з вищої точки. Де в нього найбільша швидкість?',
      'Увімкни тертя. Куди «зникає» енергія?',
    ],
  },
  {
    slug: 'energy-forms-and-changes',
    title: 'Форми енергії та її перетворення',
    about: 'Нагрівання, світло, рух: як енергія переходить з однієї форми в іншу.',
    fits: [
      [PHYSICS, 7, 8],
      [NATURE, 5, 6],
    ],
    topics: ['енергі', 'тепл'],
    tries: [
      'Нагрій воду і залізо однаково. Що нагріється швидше?',
      'Збери ланцюжок: велосипедист — генератор — лампочка.',
    ],
  },
  {
    slug: 'states-of-matter-basics',
    title: 'Стани речовини',
    about: 'Тверде тіло, рідина і газ: як рухаються частинки.',
    fits: [
      [PHYSICS, 7, 8],
      [CHEMISTRY, 7, 7],
      [NATURE, 5, 6],
    ],
    topics: ['тепл', 'речовин', 'стан', 'агрегат'],
    tries: [
      'Нагрій лід. Коли молекули води «відриваються» одна від одної?',
      'Порівняй, як рухаються частинки у твердому тілі й у газі.',
    ],
  },
  {
    slug: 'diffusion',
    title: 'Дифузія',
    about: 'Як частинки двох газів перемішуються самі собою.',
    fits: [
      [PHYSICS, 7, 7],
      [CHEMISTRY, 7, 7],
    ],
    topics: ['дифуз', 'тепл', 'будова речовин'],
    tries: [
      'Прибери перегородку. Як швидко гази змішаються?',
      'Підвищ температуру — що змінилося?',
    ],
  },
  {
    slug: 'gases-intro',
    title: 'Гази: вступ',
    about: 'Тиск, об’єм і температура газу в посудині.',
    fits: [[PHYSICS, 10, 10]],
    topics: ['газ', 'тиск', 'молекулярн'],
    tries: [
      'Зменш об’єм посудини вдвічі. Що сталося з тиском?',
      'Нагрій газ і не давай йому розширюватися.',
    ],
  },
  {
    slug: 'balloons-and-static-electricity',
    title: 'Кульки і статична електрика',
    about: 'Чому кулька після светра прилипає до стіни.',
    fits: [[PHYSICS, 8, 8]],
    topics: ['електриз', 'заряд', 'електр'],
    tries: [
      'Потри кульку об светр. Куди перейшли заряди?',
      'Піднеси наелектризовану кульку до стіни — чому вона притягується?',
    ],
  },
  {
    slug: 'john-travoltage',
    title: 'Джон Травольтаж',
    about: 'Звідки береться іскра, коли торкаєшся дверної ручки.',
    fits: [[PHYSICS, 8, 8]],
    topics: ['електриз', 'заряд'],
    tries: [
      'Потри ногою килим і доторкнися до ручки.',
      'Чому іскра проскакує, коли рука ще не торкнулася?',
    ],
  },
  {
    slug: 'circuit-construction-kit-dc',
    title: 'Лабораторія електрики: постійний струм',
    about: 'Збери власне коло з батарейок, лампочок, вимикачів і приладів.',
    fits: [[PHYSICS, 8, 11]],
    topics: ['струм', 'коло', 'електр', 'напруг', 'опір', 'практикум'],
    tries: [
      'Збери коло з батарейкою, вимикачем і лампочкою — нехай засвітиться.',
      'Додай другу лампочку послідовно. Що сталося з яскравістю?',
      'Тепер увімкни лампочки паралельно і порівняй.',
    ],
  },
  {
    slug: 'ohms-law',
    title: 'Закон Ома',
    about: 'Як сила струму залежить від напруги й опору.',
    fits: [[PHYSICS, 8, 8]],
    topics: ['закон ома', 'струм', 'напруг', 'опір'],
    tries: [
      'Збільш напругу. Як змінилася сила струму?',
      'Збільш опір удвічі за тієї ж напруги — у скільки разів зменшився струм?',
    ],
  },
  {
    slug: 'resistance-in-a-wire',
    title: 'Опір провідника',
    about: 'Від чого залежить опір дроту: довжина, площа перерізу, матеріал.',
    fits: [[PHYSICS, 8, 8]],
    topics: ['опір', 'провідник'],
    tries: ['Подовж дріт удвічі. Як змінився опір?', 'Зроби дріт товщим — опір більшає чи меншає?'],
  },
  {
    slug: 'faradays-law',
    title: 'Закон Фарадея',
    about: 'Магніт і котушка: як рух магніту створює струм.',
    fits: [[PHYSICS, 9, 11]],
    topics: ['магніт', 'індукці', 'фарадей'],
    tries: [
      'Проведи магнітом крізь котушку. Коли лампочка світить яскравіше?',
      'Порівняй котушки з двох і чотирьох витків.',
    ],
  },
  {
    slug: 'bending-light',
    title: 'Заломлення світла',
    about: 'Як промінь світла змінює напрямок на межі двох середовищ.',
    fits: [[PHYSICS, 9, 11]],
    topics: ['світл', 'заломл', 'оптик'],
    tries: [
      'Спрямуй лазер з повітря у воду. Куди відхилився промінь?',
      'Знайди кут, за якого світло вже не виходить із води.',
    ],
  },
  {
    slug: 'geometric-optics-basics',
    title: 'Геометрична оптика: основи',
    about: 'Лінзи й дзеркала: як утворюється зображення.',
    fits: [[PHYSICS, 9, 9]],
    topics: ['лінз', 'світл', 'оптик', 'дзеркал'],
    tries: [
      'Відсунь предмет від лінзи. Що сталося із зображенням?',
      'Де треба поставити предмет, щоб зображення стало перевернутим?',
    ],
  },
  {
    slug: 'wave-on-a-string',
    title: 'Хвиля на шнурі',
    about: 'Амплітуда, частота, довжина хвилі — на коливному шнурі.',
    fits: [[PHYSICS, 9, 11]],
    topics: ['хвил', 'коливан', 'звук'],
    tries: [
      'Збільш частоту. Що сталося з довжиною хвилі?',
      'Закріпи кінець шнура і подивися на відбиту хвилю.',
    ],
  },
  {
    slug: 'pendulum-lab',
    title: 'Лабораторія маятників',
    about: 'Від чого залежить період коливань маятника.',
    fits: [[PHYSICS, 9, 10]],
    topics: ['коливан', 'маятник', 'практикум'],
    tries: ['Зміни масу тягарця. Чи змінився період?', 'Перенеси маятник на Місяць. Що сталося?'],
  },
  {
    slug: 'projectile-motion',
    title: 'Рух тіла, кинутого під кутом',
    about: 'Гармата, кут і швидкість: куди впаде снаряд.',
    fits: [[PHYSICS, 9, 10]],
    topics: ['рух', 'швидк', 'кинут'],
    tries: ['Під яким кутом снаряд летить найдалі?', 'Влуч у мішень з першої спроби.'],
  },
  {
    slug: 'gravity-and-orbits',
    title: 'Гравітація і орбіти',
    about: 'Сонце, Земля і Місяць: чому планети не відлітають.',
    fits: [
      [PHYSICS, 9, 11],
      [NATURE, 5, 6],
      [GEOGRAPHY, 7, 7],
    ],
    topics: ['всесвіт', 'планет', 'гравітац', 'тяжін', 'земля'],
    tries: ['Вимкни гравітацію. Куди полетить Земля?', 'Збільш масу Сонця — як змінилася орбіта?'],
  },
  {
    slug: 'build-a-nucleus',
    title: 'Побудуй ядро',
    about: 'Протони й нейтрони: які ядра стабільні, а які розпадаються.',
    fits: [[PHYSICS, 9, 11]],
    topics: ['ядр', 'радіоакт', 'атом'],
    tries: ['Збери ядро Гелію-4.', 'Додай забагато нейтронів — що станеться з ядром?'],
  },
  // --- Хімія ------------------------------------------------------------------------------------
  {
    slug: 'build-an-atom',
    title: 'Будуємо атом',
    about: 'Протони, нейтрони й електрони: збери атом і йон.',
    fits: [
      [CHEMISTRY, 7, 8],
      [PHYSICS, 9, 9],
    ],
    topics: ['атом', 'елемент', 'періодичн', 'будова'],
    tries: [
      'Збери атом Карбону. Скільки протонів потрібно?',
      'Додай зайвий електрон. Як змінився заряд?',
    ],
  },
  {
    slug: 'isotopes-and-atomic-mass',
    title: 'Ізотопи та атомна маса',
    about: 'Чому в таблиці Менделєєва атомні маси дробові.',
    fits: [[CHEMISTRY, 8, 9]],
    topics: ['ізотоп', 'атом', 'періодичн', 'маса'],
    tries: ['Зроби ізотоп Карбону-14.', 'Змішай ізотопи Хлору так, щоб середня маса була 35,5.'],
  },
  {
    slug: 'build-a-molecule',
    title: 'Побудуй молекулу',
    about: 'З’єднуй атоми в молекули і дивися на них у 3D.',
    fits: [[CHEMISTRY, 7, 7]],
    topics: ['формул', 'молекул', 'речовин'],
    tries: ['Збери молекулу води H₂O.', 'А тепер — вуглекислий газ CO₂.'],
  },
  {
    slug: 'molecule-shapes-basics',
    title: 'Форми молекул',
    about: 'Чому молекули мають саме таку форму і кути між зв’язками.',
    fits: [[CHEMISTRY, 9, 11]],
    topics: ['молекул', 'зв’яз', "зв'яз", 'будова'],
    tries: [
      'Додай до центрального атома чотири зв’язки. Який кут між ними?',
      'Порівняй форму молекул води та вуглекислого газу.',
    ],
  },
  {
    slug: 'balancing-chemical-equations',
    title: 'Хімічні рівняння',
    about: 'Розстав коефіцієнти, щоб атомів зліва і справа було порівну.',
    fits: [[CHEMISTRY, 7, 8]],
    topics: ['реакці', 'рівнян'],
    tries: ['Зрівняй рівняння утворення води.', 'Пройди перший рівень гри без жодної помилки.'],
  },
  {
    slug: 'reactants-products-and-leftovers',
    title: 'Реагенти, продукти і залишки',
    about: 'Скільки продукту вийде і чого залишиться забагато.',
    fits: [[CHEMISTRY, 8, 9]],
    topics: ['реакці', 'надлишок'],
    tries: [
      'Зроби бутерброди: скільки вийде з 5 скибок хліба і 3 шматків сиру?',
      'Те саме з молекулами: що залишиться після реакції?',
    ],
  },
  {
    slug: 'concentration',
    title: 'Концентрація',
    about: 'Розчиняй, випаровуй і розбавляй: як змінюється концентрація.',
    fits: [[CHEMISTRY, 7, 9]],
    topics: ['розчин', 'концентрац', 'речовин'],
    tries: [
      'Досипай солі, доки розчин не стане насиченим.',
      'Долий води. Що сталося з концентрацією?',
    ],
  },
  {
    slug: 'ph-scale-basics',
    title: 'Шкала pH',
    about: 'Кислоти й основи довкола нас: сік, кава, мило.',
    fits: [[CHEMISTRY, 7, 9]],
    topics: ['кислот', 'основ', 'розчин', 'ph'],
    tries: ['Що кисліше — апельсиновий сік чи кава?', 'Розбав мило водою. Як змінився pH?'],
  },
  {
    slug: 'acid-base-solutions',
    title: 'Розчини кислот і основ',
    about: 'Сильні й слабкі кислоти та основи на рівні молекул.',
    fits: [[CHEMISTRY, 9, 11]],
    topics: ['кислот', 'основ', 'електроліт', 'розчин'],
    tries: [
      'Порівняй провідність сильної і слабкої кислоти.',
      'Яка з основ змінює колір індикатора сильніше?',
    ],
  },
  // --- Математика -------------------------------------------------------------------------------
  {
    slug: 'make-a-ten',
    title: 'Влуч у десятку!',
    about: 'Додавання через десяток на кольорових картках.',
    fits: [[MATH, 1, 2]],
    topics: ['додаван', 'десят'],
    tries: ['Склади 8 + 5 через десяток.', 'Пройди перший рівень гри.'],
  },
  {
    slug: 'arithmetic',
    title: 'Арифметика',
    about: 'Таблиця множення і ділення як гра.',
    fits: [[MATH, 2, 4]],
    topics: ['множен', 'ділен', 'таблиц'],
    tries: ['Заповни таблицю множення на 7.', 'Спробуй рівень з діленням.'],
  },
  {
    slug: 'fractions-intro',
    title: 'Дроби: вступ',
    about: 'Чисельник і знаменник на піцах, склянках і числовій прямій.',
    fits: [[MATH, 4, 5]],
    topics: ['дроб'],
    tries: ['Покажи 3/4 на піці.', 'Що більше: 2/3 чи 3/5?'],
  },
  {
    slug: 'build-a-fraction',
    title: 'Будуємо дроби',
    about: 'Збирай дроби з частинок — по-різному.',
    fits: [[MATH, 5, 6]],
    topics: ['дроб'],
    tries: ['Збери 1/2 трьома різними способами.', 'Пройди рівень зі змішаними числами.'],
  },
  {
    slug: 'area-builder',
    title: 'Будівник площі',
    about: 'Будуй фігури з квадратиків: площа і периметр.',
    fits: [[MATH, 3, 6]],
    topics: ['площ', 'периметр', 'фігур'],
    tries: [
      'Побудуй фігуру з площею 12 і найменшим периметром.',
      'Знайди дві різні фігури з однаковою площею.',
    ],
  },
  {
    slug: 'number-line-integers',
    title: 'Числова пряма: цілі числа',
    about: 'Додатні й від’ємні числа: термометр, банк, висота.',
    fits: [[MATH, 6, 6]],
    topics: ['від’ємн', "від'ємн", 'цілі числ', 'координат'],
    tries: [
      'Познач на прямій −3 і 5. Яка між ними відстань?',
      'Порівняй −7 і −2 — яке число більше?',
    ],
  },
  {
    slug: 'proportion-playground',
    title: 'Гра в пропорції',
    about: 'Намиста, фарби й торти: що таке однакове відношення.',
    fits: [[MATH, 6, 6]],
    topics: ['пропорц', 'відношен', 'відсот'],
    tries: [
      'Змішай фарбу так, щоб відтінок не змінився, коли її стало більше.',
      'Яка коробка вигідніша?',
    ],
  },
  {
    slug: 'equality-explorer',
    title: 'Дослідник рівностей',
    about: 'Рівняння як терези: що робиш з одного боку — роби й з іншого.',
    fits: [
      [MATH, 6, 6],
      [ALGEBRA, 7, 7],
    ],
    topics: ['рівнян'],
    tries: ['Розв’яжи на терезах 3x + 2 = 11.', 'Пройди рівень гри з від’ємними числами.'],
  },
  {
    slug: 'expression-exchange',
    title: 'Перетвори вираз',
    about: 'Збирай подібні доданки, як монети.',
    fits: [[ALGEBRA, 7, 7]],
    topics: ['вираз', 'тотожн', 'подібн', 'одночлен'],
    tries: ['Спрости 2x + 3y + x.', 'Пройди рівень зі змінними x² і xy.'],
  },
  {
    slug: 'area-model-algebra',
    title: 'Площинна модель: алгебра',
    about: 'Множення многочленів як площа прямокутника.',
    fits: [[ALGEBRA, 7, 7]],
    topics: ['многочлен', 'одночлен', 'скороченого', 'множник'],
    tries: [
      'Покажи (x + 2)(x + 3) як площу прямокутника.',
      'Побач, звідки береться формула (a + b)².',
    ],
  },
  {
    slug: 'graphing-lines',
    title: 'Графік лінійної функції',
    about: 'Як кутовий коефіцієнт і зсув змінюють пряму.',
    fits: [[ALGEBRA, 7, 8]],
    topics: ['функці', 'граф', 'систем'],
    tries: [
      'Побудуй пряму y = 2x + 1.',
      'Зроби дві прямі, що не перетинаються. Що в них спільного?',
    ],
  },
  {
    slug: 'graphing-quadratics',
    title: 'Графік квадратичної функції',
    about: 'Парабола: вершина, корені й вісь симетрії.',
    fits: [[ALGEBRA, 8, 9]],
    topics: ['квадрат', 'парабол', 'функці'],
    tries: ['Зсунь параболу на 3 вгору.', 'Знайди корені y = x² − 4.'],
  },
  {
    slug: 'plinko-probability',
    title: 'Ймовірність: дошка Гальтона',
    about: 'Кульки падають крізь кілки — звідки береться «дзвін».',
    fits: [
      [ALGEBRA, 9, 9],
      [CALCULUS, 11, 11],
    ],
    topics: ['ймовірн', 'статист'],
    tries: ['Кинь 100 кульок. Куди їх потрапило найбільше?', 'Зміни ймовірність відскоку.'],
  },
  {
    slug: 'trig-tour',
    title: 'Тригонометрична подорож',
    about: 'Синус, косинус і тангенс на одиничному колі.',
    fits: [[CALCULUS, 10, 10]],
    topics: ['тригонометр', 'синус', 'косинус', 'кут'],
    tries: ['Знайди кут, за якого sin = cos.', 'Простеж графік синуса за повний оберт.'],
  },
  {
    slug: 'vector-addition',
    title: 'Додавання векторів',
    about: 'Вектори на площині: сума, різниця, проєкції.',
    fits: [
      [GEOMETRY, 9, 9],
      [PHYSICS, 9, 9],
    ],
    topics: ['вектор'],
    tries: [
      'Склади два вектори правилом трикутника.',
      'Знайди вектор, який у сумі з даним дає нуль.',
    ],
  },
  // --- Біологія ---------------------------------------------------------------------------------
  {
    slug: 'natural-selection',
    title: 'Природний добір',
    about: 'Кролики, вовки й мутації: як змінюється популяція.',
    fits: [
      [BIOLOGY, 9, 9],
      [BIOLOGY_ECO, 10, 11],
    ],
    topics: ['еволюц', 'добір', 'мінлив', 'популяц'],
    tries: [
      'Додай вовків і мутацію коричневого хутра. Хто виживе в пустелі?',
      'А тепер перенеси популяцію в Арктику.',
    ],
  },
  {
    slug: 'gene-expression-essentials',
    title: 'Експресія генів',
    about: 'Від гена до білка: транскрипція і трансляція.',
    fits: [[BIOLOGY_ECO, 10, 11]],
    topics: ['ген', 'спадков', 'білок'],
    tries: ['Синтезуй свій перший білок.', 'Що змінюється, коли більше РНК-полімерази?'],
  },
  {
    slug: 'neuron',
    title: 'Нейрон',
    about: 'Як нервовий імпульс біжить по нервовій клітині.',
    fits: [[BIOLOGY, 8, 9]],
    topics: ['нерв', 'людин', 'клітин'],
    tries: ['Запусти імпульс і простеж за йонами.', 'Подивися, що відбувається з потенціалом.'],
  },
  {
    slug: 'color-vision',
    title: 'Як ми бачимо колір',
    about: 'Червоне, зелене і синє світло змішуються в оці.',
    fits: [
      [BIOLOGY, 8, 9],
      [PHYSICS, 9, 9],
      [NATURE, 5, 6],
    ],
    topics: ['світл', 'зір', 'око', 'колір', 'людин'],
    tries: ['Змішай світло так, щоб побачити жовтий.', 'Як фільтр змінює колір, який ми бачимо?'],
  },
];

const BY_SLUG = new Map(LAB_SIMS.map((s) => [s.slug, s]));

export function labSim(slug: string): LabSim | undefined {
  return BY_SLUG.get(slug);
}

/** The simulation itself, in Ukrainian. */
export function simUrl(slug: string): string {
  return `${PHET_BASE}/${slug}/latest/${slug}_uk.html`;
}

/** Its picture (600 px wide), for cards. */
export function simImage(slug: string): string {
  return `${PHET_BASE}/${slug}/latest/${slug}-600.png`;
}

/** A material that is a link to one of our simulations. */
export function simOfUrl(url: string | null | undefined): LabSim | undefined {
  const slug = url?.match(/^https:\/\/phet\.colorado\.edu\/sims\/html\/([a-z0-9-]+)\//)?.[1];
  return slug === undefined ? undefined : BY_SLUG.get(slug);
}

/** «8–11 клас» or «7 клас»: the classes an item fits in one subject. */
export function fitLabel([, from, to]: LabFit): string {
  return from === to ? `${from} клас` : `${from}–${to} клас`;
}

function fitsClass(fits: readonly LabFit[], subject?: string, grade?: number) {
  return fits.some(
    ([s, from, to]) =>
      (subject === undefined || s === subject) &&
      (grade === undefined || (grade >= from && grade <= to)),
  );
}

/** The catalog for a subject and/or a class, in the catalog's order. */
export function simsFor(filter: { subject?: string; grade?: number } = {}): LabSim[] {
  return LAB_SIMS.filter((s) => fitsClass(s.fits, filter.subject, filter.grade));
}

function topicScore(stems: readonly string[], topic: string): number {
  const text = topic.toLowerCase();
  return stems.filter((stem) => text.includes(stem)).length;
}

/**
 * What fits a lesson: the subject first, then how many stems of the topic match. Without a match
 * by topic, nothing is suggested — a random simulation is no help.
 */
export function suggestSims(lesson: { subject: string; topic: string | null; grade?: number }) {
  if (!lesson.topic) return [];
  return LAB_SIMS.filter((s) => fitsClass(s.fits, lesson.subject))
    .map((s) => ({
      s,
      score: topicScore(s.topics, lesson.topic as string),
      // the class fits too: first
      near: lesson.grade === undefined || fitsClass(s.fits, lesson.subject, lesson.grade),
    }))
    .filter((x) => x.score > 0)
    .sort((a, b) => Number(b.near) - Number(a.near) || b.score - a.score)
    .map((x) => x.s);
}

/** Subjects of the catalog (for a class, if given), sims and models together, in a stable order. */
export function labSubjects(grade?: number): string[] {
  const all = [...LAB_SIMS, ...LAB_MODELS]
    .flatMap((x) =>
      x.fits.filter(([, from, to]) => grade === undefined || (grade >= from && grade <= to)),
    )
    .map(([s]) => s);
  return [...new Set(all)].sort((a, b) => a.localeCompare(b, 'uk'));
}

// ---------------------------------------------------------------------------------------------
// 3D models: drawn by the app itself (no downloaded files), viewable in AR on iPhone
// ---------------------------------------------------------------------------------------------

export type LabModelId = 'solar-system' | 'saturn' | 'molecules' | 'atom' | 'solids' | 'cell';

export interface LabModel {
  id: LabModelId;
  title: string;
  about: string;
  emoji: string;
  fits: readonly LabFit[];
  topics: readonly string[];
}

export const LAB_MODELS: readonly LabModel[] = [
  {
    id: 'solar-system',
    title: 'Сонячна система',
    about: 'Покрути планети, порівняй їхні розміри й дізнайся, яка там доба і рік.',
    emoji: '🪐',
    fits: [
      [NATURE, 5, 6],
      [GEOGRAPHY, 7, 7],
      [PHYSICS, 11, 11],
    ],
    topics: ['всесвіт', 'планет', 'сонц', 'земля'],
  },
  {
    id: 'saturn',
    title: 'Сатурн на столі',
    about: 'Планета з кільцями — роздивися зблизька або постав на стіл через камеру iPhone.',
    emoji: '💫',
    fits: [
      [NATURE, 5, 6],
      [GEOGRAPHY, 7, 7],
      [PHYSICS, 11, 11],
    ],
    topics: ['всесвіт', 'планет'],
  },
  {
    id: 'molecules',
    title: 'Молекули',
    about: 'Вода, вуглекислий газ, метан, амоніак і кристал солі — атоми і кути між зв’язками.',
    emoji: '💧',
    fits: [
      [CHEMISTRY, 7, 9],
      [NATURE, 6, 6],
    ],
    topics: ['формул', 'молекул', 'речовин'],
  },
  {
    id: 'atom',
    title: 'Модель атома',
    about: 'Ядро з протонів і нейтронів та електрони на енергетичних рівнях.',
    emoji: '⚛️',
    fits: [
      [CHEMISTRY, 7, 8],
      [PHYSICS, 9, 9],
    ],
    topics: ['атом', 'елемент', 'періодичн', 'будова'],
  },
  {
    id: 'solids',
    title: 'Геометричні тіла',
    about: 'Куб, призма, піраміда, циліндр, конус і куля — вершини, ребра, грані й розгортки.',
    emoji: '🧊',
    fits: [
      [MATH, 5, 6],
      [GEOMETRY, 10, 11],
    ],
    topics: ['фігур', 'тіла', 'многогранник', 'об’єм', "об'єм", 'площ'],
  },
  {
    id: 'cell',
    title: 'Клітина',
    about: 'Рослинна і тваринна клітина: ядро, мітохондрії, хлоропласти, вакуоля.',
    emoji: '🦠',
    fits: [
      [BIOLOGY, 7, 7],
      [NATURE, 5, 6],
    ],
    topics: ['клітин'],
  },
];

export function labModel(id: string): LabModel | undefined {
  return LAB_MODELS.find((m) => m.id === id);
}

export function modelsFor(filter: { subject?: string; grade?: number } = {}): LabModel[] {
  return LAB_MODELS.filter((m) => fitsClass(m.fits, filter.subject, filter.grade));
}
